import React from 'react';

interface ValidationProgressBarProps {
  total: number;
  editedCount: number;
  excludedCount: number;
  unreviewedCount: number;
}

export const ValidationProgressBar: React.FC<ValidationProgressBarProps> = ({
  total,
  editedCount,
  excludedCount,
  unreviewedCount,
}) => {
  const reviewed = total - unreviewedCount;
  const pctEdited = total > 0 ? (editedCount / total) * 100 : 0;
  const pctExcluded = total > 0 ? (excludedCount / total) * 100 : 0;
  const pctUnedited = total > 0 ? ((reviewed - editedCount - excludedCount) / total) * 100 : 0;
  const pctUnreviewed = total > 0 ? (unreviewedCount / total) * 100 : 0;

  return (
    <div data-testid="validation-progress-bar">
      <div className="flex justify-between text-xs text-text-tertiary mb-1">
        <span>
          {reviewed.toLocaleString()} / {total.toLocaleString()} reviewed
        </span>
        <span>{unreviewedCount.toLocaleString()} unreviewed remaining</span>
      </div>
      <div className="w-full h-3 bg-bg-hover rounded-full overflow-hidden flex">
        {/* Approved (unedited) — green */}
        <div
          className="bg-qc-success h-full transition-all duration-300"
          style={{ width: `${pctUnedited}%` }}
          title={`${reviewed - editedCount - excludedCount} approved`}
        />
        {/* Edited (override) — blue */}
        <div
          className="bg-interactive-primary h-full transition-all duration-300"
          style={{ width: `${pctEdited}%` }}
          title={`${editedCount} edited`}
        />
        {/* Excluded — red */}
        <div
          className="bg-qc-error h-full transition-all duration-300"
          style={{ width: `${pctExcluded}%` }}
          title={`${excludedCount} excluded`}
        />
        {/* Unreviewed — grey (remainder) */}
        <div
          className="bg-bg-hover h-full transition-all duration-300"
          style={{ width: `${pctUnreviewed}%` }}
          title={`${unreviewedCount} unreviewed`}
        />
      </div>
      <div className="flex gap-4 mt-1 text-xs text-text-tertiary">
        <span className="flex items-center gap-1">
          <span className="inline-block w-2 h-2 rounded-full bg-qc-success" />
          Approved ({(reviewed - editedCount - excludedCount).toLocaleString()})
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block w-2 h-2 rounded-full bg-interactive-primary" />
          Edited ({editedCount.toLocaleString()})
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block w-2 h-2 rounded-full bg-qc-error" />
          Excluded ({excludedCount.toLocaleString()})
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block w-2 h-2 rounded-full bg-border-strong" />
          Unreviewed ({unreviewedCount.toLocaleString()})
        </span>
      </div>
    </div>
  );
};
